'use client';

import { useTranslations } from 'next-intl';
import { PrintPortal } from '@/components/print-portal';
import {
  PrintSection,
  PrintGrid,
  PrintField
} from '@/components/print-helpers';
import type {
  Registration,
  RegistrationActivity
} from '@/features/registration';
import type { DynamicFormField } from '@/features/dynamic-forms';

interface ReviewPrintViewProps {
  registration: Registration;
  fields: DynamicFormField[];
  activities: RegistrationActivity[];
}

export function ReviewPrintView({
  registration,
  fields,
  activities
}: ReviewPrintViewProps) {
  const t = useTranslations('review');

  return (
    <PrintPortal>
      <div
        style={{
          fontFamily: 'Arial, sans-serif',
          padding: '30px',
          maxWidth: '800px',
          margin: '0 auto'
        }}
      >
        <h1
          style={{
            fontSize: '18px',
            fontWeight: 700,
            marginBottom: '6px'
          }}
        >
          {registration.container_number}
        </h1>
        <p style={{ fontSize: '11px', color: '#888', marginBottom: '20px' }}>
          {registration.entity_name_ar || registration.entity_name_en}
        </p>

        <PrintSection title={t('registrationInfo')}>
          <PrintGrid>
            <PrintField
              label={t('entityName')}
              value={
                registration.entity_name_ar ||
                registration.entity_name_en ||
                '\u2014'
              }
            />
            <PrintField
              label={t('containerType')}
              value={registration.container_type?.name ?? '\u2014'}
            />
            <PrintField
              label={t('totalPages')}
              value={
                registration.expected_page_count != null
                  ? String(registration.expected_page_count)
                  : '\u2014'
              }
            />
          </PrintGrid>
        </PrintSection>

        {registration.form_responses &&
          Object.keys(registration.form_responses).length > 0 && (
            <PrintSection title={t('registrationForm')}>
              <PrintGrid>
                {fields.map((field) => {
                  const val = registration.form_responses?.[String(field.id)];
                  return (
                    <PrintField
                      key={field.id}
                      label={field.label}
                      value={val != null ? String(val) : '\u2014'}
                    />
                  );
                })}
              </PrintGrid>
            </PrintSection>
          )}

        {activities.length > 0 && (
          <PrintSection title={t('reviewLog')}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '11px'
              }}
            >
              <tbody>
                {activities.map((a) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '4px 8px' }}>{a.action}</td>
                    <td style={{ padding: '4px 8px' }}>
                      {a.user?.name ?? '\u2014'}
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      {new Date(a.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </PrintSection>
        )}
      </div>
    </PrintPortal>
  );
}
